import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Helmet } from 'react-helmet';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'wouter';
import { GAME_MODE, SingSetup, SongPreview } from '~/interfaces';
import CameraManager from '~/modules/Camera/CameraManager';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import NoPrerender from '~/modules/Elements/NoPrerender';
import MultiplayerManager from '~/modules/Multiplayer/MultiplayerManager';
import RoomStateStore, { countdownStarted, kicked, roomError, roomStateChanged } from '~/modules/Multiplayer/RoomState';
import Chat from '~/modules/Multiplayer/components/Chat';
import { useIsHost, useIsInRoom, useReconnecting } from '~/modules/Multiplayer/hooks/useIsHost';
import { useRoom } from '~/modules/Multiplayer/hooks/useRoom';
import type { RoomData } from '~/modules/Multiplayer/messages';
import PlayersManager from '~/modules/Players/PlayersManager';
import { woosh } from '~/modules/SoundManager';
import useFullscreen from '~/modules/hooks/useFullscreen';
import useQueryParam from '~/modules/hooks/useQueryParam';
import startViewTransition from '~/modules/utils/startViewTransition';
import { ExcludedLanguagesSetting } from '~/routes/Settings/SettingsState';
import SingASong from '~/routes/SingASong/SingASong';
import Singing from './Singing/Singing';

const DIFFICULTY_LABELS: Record<number, string> = { 1: 'Hard', 2: 'Medium', 3: 'Easy' };

function WaitingForHost() {
  useBackground(true);
  const room = useRoom();
  const players = room?.players ?? [];
  const currentSong = room?.currentSong;
  const roomState = room?.state;
  const songQueue = room?.songQueue ?? [];
  const tolerance = room?.tolerance ?? 2;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-4">
      <Typography className="text-3xl font-bold tracking-wider uppercase">Karaoke Party</Typography>
      <Box className="gap-4 bg-black/75 p-8">
        {roomState === 'SCORES' ? (
          <>
            <Typography className="text-xl">Czekam na następną piosenkę...</Typography>
            <Typography className="text-white/60">Host wybiera kolejny utwór</Typography>
          </>
        ) : roomState === 'COUNTDOWN' || roomState === 'PLAYING' ? (
          <>
            <Typography className="text-xl">Ładowanie gry...</Typography>
            {currentSong && (
              <div className="flex flex-col items-center gap-1">
                <Typography className="text-lg text-white/80">
                  {currentSong.artist} - {currentSong.title}
                </Typography>
                <span className="rounded-full bg-white/10 px-3 py-0.5 text-xs text-white/50">
                  {DIFFICULTY_LABELS[tolerance] ?? `Tolerance ${tolerance}`}
                </span>
              </div>
            )}
            {/* Ready indicators */}
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {players
                .filter((p) => p.isConnected)
                .map((p) => (
                  <span
                    key={p.id}
                    className={`rounded-full px-3 py-1 text-xs transition-colors ${
                      p.isReady ? 'bg-green-500/30 text-green-300' : 'bg-white/10 text-white/40'
                    }`}>
                    {p.isReady ? '✓' : '...'} {p.name}
                  </span>
                ))}
            </div>
          </>
        ) : (
          <>
            <Typography className="text-xl">Czekam na hosta...</Typography>
            <Typography className="text-white/60">Host wybiera piosenkę</Typography>
          </>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {players.map((p) => (
            <span
              key={p.id}
              className={`rounded-full px-3 py-1 text-sm ${p.isHost ? 'bg-yellow-500/30 text-yellow-300' : 'bg-white/10 text-white/70'} ${!p.isConnected ? 'opacity-40' : ''}`}>
              {p.name}
              {p.isHost ? ' (Host)' : ''}
            </span>
          ))}
        </div>

        {/* Song queue visible to all */}
        {songQueue.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            <Typography className="text-xs font-semibold tracking-wider text-white/40 uppercase">
              Kolejka ({songQueue.length})
            </Typography>
            {songQueue.slice(0, 5).map((song, i) => (
              <div key={`${song.id}-${i}`} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
                <span className="text-xs text-white/30">{i + 1}.</span>
                <span className="truncate text-sm text-white/70">
                  {song.artist} - {song.title}
                </span>
              </div>
            ))}
            {songQueue.length > 5 && (
              <Typography className="text-xs text-white/30">...i {songQueue.length - 5} więcej</Typography>
            )}
          </div>
        )}
      </Box>
    </div>
  );
}

function Game() {
  const [, setLocation] = useLocation();
  const songId = useQueryParam('song');
  const isMultiplayer = useIsInRoom();
  const isHost = useIsHost();
  const isReconnecting = useReconnecting();

  // In multiplayer mode, auto-set language preferences to skip the selection screen
  useEffect(() => {
    if (isMultiplayer && ExcludedLanguagesSetting.get() === null) {
      ExcludedLanguagesSetting.set([]);
    }
  }, [isMultiplayer]);

  const [singSetup, setSingSetup] = useState<(SingSetup & { song: SongPreview }) | null>(null);
  const [preselectedSong, setPreselectedSong] = useState<string | null>(songId ?? null);
  const [resetKey, setResetKey] = useState(0);
  const [reconnectTimeout, setReconnectTimeout] = useState(false);

  useEffect(() => {
    if (!isReconnecting) {
      setReconnectTimeout(false);
      return;
    }
    const id = setTimeout(() => setReconnectTimeout(true), 20000);
    return () => clearTimeout(id);
  }, [isReconnecting]);

  // Configure microphone and player name for multiplayer
  const setupMultiplayerPlayer = useCallback(() => {
    // Remove extra players, keep only player 0
    const players = PlayersManager.getPlayers();
    players.filter((p) => p.number !== 0).forEach((p) => PlayersManager.removePlayer(p.number));

    // Ensure player 0 exists
    let player0 = PlayersManager.getPlayer(0);
    if (!player0) {
      player0 = PlayersManager.addPlayer(0)!;
    }

    // Force local microphone input with a valid deviceId
    // Always call changeInput: even if source is already 'Microphone', the deviceId might
    // be undefined (e.g. from a previous local session), which causes MultiMicInput to
    // skip startMonitoring and return [0,0] frequencies → score stays at 0.
    if (player0.input.source !== 'Microphone' || !player0.input.deviceId) {
      player0.changeInput('Microphone', 0, player0.input.deviceId || 'default');
    }

    // Set player name from room
    if (RoomStateStore.myPlayer) {
      player0.setName(RoomStateStore.myPlayer.name);
    }
  }, []);

  const handleSelect = (setup: SingSetup & { song: SongPreview }) => {
    // In multiplayer mode, host selects song and starts countdown via server
    if (isMultiplayer && isHost) {
      MultiplayerManager.selectSong(setup.song);
      setupMultiplayerPlayer();
      const multiplayerSetup: SingSetup & { song: SongPreview } = {
        ...setup,
        mode: GAME_MODE.MULTIPLAYER,
        players: [{ track: 0, number: 0 }],
      };
      MultiplayerManager.startCountdown(setup.tolerance);
      setSingSetup(multiplayerSetup);
      woosh.play(false);
      return;
    }

    const previewVideo = document.getElementById('preview-video-container');

    if (previewVideo) {
      previewVideo.style.viewTransitionName = 'song-preview-video';
      startViewTransition(() => {
        previewVideo.style.viewTransitionName = '';
        flushSync(() => {
          setSingSetup(setup);
        });
      });
    } else {
      setSingSetup(setup);
    }

    woosh.play(false);
  };

  // Non-host players: persistently listen for countdown events across all songs
  useEffect(() => {
    if (!isMultiplayer || isHost) return;

    let lastSetupRound = -1;

    const createSetup = (song: SongPreview, tolerance?: number) => {
      const currentRound = RoomStateStore.room?.roundNumber ?? 0;
      // Guard against duplicate setups for the same round
      if (currentRound === lastSetupRound && currentRound > 0) return;
      lastSetupRound = currentRound;

      setupMultiplayerPlayer();
      const multiplayerSetup: SingSetup & { song: SongPreview } = {
        id: uuidv4(),
        players: [{ track: 0, number: 0 }],
        mode: GAME_MODE.MULTIPLAYER,
        tolerance: tolerance ?? RoomStateStore.room?.tolerance ?? 2,
        song,
      };
      setSingSetup(multiplayerSetup);
      setResetKey((k) => k + 1);
    };

    // If song was already selected before this component mounted (race condition)
    const currentSong = RoomStateStore.currentSong;
    const roomState = RoomStateStore.state;
    if (currentSong && (roomState === 'COUNTDOWN' || roomState === 'PLAYING')) {
      createSetup(currentSong, RoomStateStore.room?.tolerance);
    }

    countdownStarted.subscribe(createSetup);
    return () => countdownStarted.unsubscribe(createSetup);
  }, [isMultiplayer, isHost, setupMultiplayerPlayer]);

  // Navigate back to lobby when room state changes to LOBBY, or handle disconnect/kick/error
  useEffect(() => {
    if (!isMultiplayer) return;

    const handleRoomChange = (room: RoomData | null) => {
      if (!room) {
        // Room cleared (disconnect, expired, etc.)
        setSingSetup(null);
        setLocation('');
        return;
      }
      if (room.state === 'LOBBY') {
        setSingSetup(null);
        setLocation(`room/${room.code}`);
      }
    };

    const handleKicked = () => {
      setSingSetup(null);
      setLocation('/');
    };

    const handleError = () => {
      setSingSetup(null);
      setLocation('/');
    };

    roomStateChanged.subscribe(handleRoomChange);
    kicked.subscribe(handleKicked);
    roomError.subscribe(handleError);

    return () => {
      roomStateChanged.unsubscribe(handleRoomChange);
      kicked.unsubscribe(handleKicked);
      roomError.unsubscribe(handleError);
    };
  }, [isMultiplayer, setLocation]);

  useFullscreen();

  // Determine what to show for non-host when there's no singSetup
  const showWaitingForHost = isMultiplayer && !isHost && !singSetup;

  return (
    <>
      <Helmet>
        <title>{isMultiplayer ? 'Karaoke Party' : 'Game | AllKaraoke.Party - Free Online Karaoke Party Game'}</title>
      </Helmet>
      <NoPrerender>
        {isReconnecting && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 rounded-xl bg-black/90 px-8 py-6 ring-1 ring-white/20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              <span className="text-lg text-white">Ponowne łączenie...</span>
              <span className="text-sm text-white/50">Nie zamykaj strony</span>
              {reconnectTimeout && (
                <button
                  onClick={() => {
                    MultiplayerManager.leaveRoom();
                    setLocation('');
                  }}
                  className="mt-2 rounded-lg bg-red-500/30 px-4 py-2 text-sm text-red-200 transition-colors hover:bg-red-500/50">
                  Opuść pokój
                </button>
              )}
            </div>
          </div>
        )}
        {isMultiplayer && <Chat compact />}
        {singSetup ? (
          <Singing
            restartSong={() => {
              CameraManager.restartRecord();
              setResetKey((current) => current + 1);
            }}
            key={resetKey}
            songPreview={singSetup.song}
            singSetup={singSetup}
            returnToSongSelection={() => {
              setPreselectedSong(singSetup.song.id);
              setSingSetup(null);
            }}
          />
        ) : showWaitingForHost ? (
          <WaitingForHost />
        ) : (
          <SingASong onSongSelected={handleSelect} preselectedSong={preselectedSong} />
        )}
      </NoPrerender>
    </>
  );
}
export default Game;
